"use client";

import Link, { type LinkProps } from "next/link";
import { forwardRef, type ComponentPropsWithoutRef, type MouseEvent } from "react";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";

type TrackedLinkProps = LinkProps &
    Omit<ComponentPropsWithoutRef<"a">, keyof LinkProps> & {
        event?: string;
        cleverTapEvent?: string;
        cleverTapProps?: Record<string, unknown>;
    };

const hrefToString = (href: LinkProps["href"]): string => {
    if (typeof href === "string") return href;
    if (href.pathname) return href.pathname;
    return "";
};

const TrackedLink = forwardRef<HTMLAnchorElement, TrackedLinkProps>(
    (
        { href, event, cleverTapEvent, cleverTapProps, onClick, children, ...rest },
        ref,
    ) => {
        const { cleverTapTrackEvent } = useCleverTapEvents();

        const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
            if (event) {
                void setAnalyticsEvent(hrefToString(href), event);
            }
            if (cleverTapEvent) {
                cleverTapTrackEvent(cleverTapEvent, cleverTapProps ?? {});
            }
            onClick?.(e);
        };

        return (
            <Link ref={ref} href={href} onClick={handleClick} {...rest}>
                {children}
            </Link>
        );
    },
);

TrackedLink.displayName = "TrackedLink";

export default TrackedLink;
